import { X, Printer, Tag, MessageCircle, TruckIcon } from "lucide-react";
import { fmtBRL, STATUS_CONFIG, PAY_LABELS, waLink } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { printOrderOS, printOrderLabel } from "./printOS";
import OrderPhotos from "../components/OrderPhotos";

export default function OrderDetailModal({ order, onClose, onExit, onUpdate, onRefresh }) {
  const { user } = useAuth();
  const canSeeCost = user?.role === "admin";
  const cfg = STATUS_CONFIG[order.status];

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      data-testid="order-detail-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <div>
            <div className="text-xs text-white/60 uppercase tracking-wider font-semibold">
              Ordem de Serviço
            </div>
            <h3 className="font-heading text-lg font-bold">{order.orderNumber} · {order.clientName}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          <div className="flex items-center justify-between">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text} ${cfg.border}`}
            >
              {cfg.label}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => printOrderOS(order)}
                className="h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5"
              >
                <Printer className="w-4 h-4" /> Imprimir OS
              </button>
              <button
                onClick={() => printOrderLabel(order)}
                className="h-9 px-3 rounded-lg border border-purple-300 text-sm font-medium text-purple-700 hover:bg-purple-50 inline-flex items-center gap-1.5"
              >
                <Tag className="w-4 h-4" /> Etiqueta
              </button>
              {order.status === "ready" && (
                <a
                  href={waLink(order.clientPhone, `Olá ${order.clientName}, seu ${order.model} está pronto para retirada! 🎉`)}
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 px-3 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {order.status !== "delivered" && (
                <button
                  onClick={onExit}
                  className="h-9 px-3 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
                >
                  <TruckIcon className="w-4 h-4" /> Registrar saída
                </button>
              )}
            </div>
          </div>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
              <div className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider mb-2">
                Cliente
              </div>
              <div className="text-sm font-semibold text-slate-800">{order.clientName}</div>
              <div className="text-sm text-slate-500">{order.clientPhone}</div>
            </div>
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
              <div className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider mb-2">
                Aparelho
              </div>
              <div className="text-sm font-semibold text-slate-800">
                {order.brand} {order.model}
              </div>
              <div className="text-xs text-slate-500">
                Cor: {order.color || "—"} · IMEI: {order.imei || "—"}
              </div>
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider mb-2">
              Serviços
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(order.services || []).map((s) => (
                <span
                  key={s}
                  className="text-xs px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 font-medium"
                >
                  {s}
                </span>
              ))}
            </div>
          </section>

          {(order.problem || order.observation) && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {order.problem && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="text-[11px] uppercase font-semibold text-slate-400 mb-1">
                    Problema
                  </div>
                  <div className="text-sm text-slate-700">{order.problem}</div>
                </div>
              )}
              {order.observation && (
                <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/50">
                  <div className="text-[11px] uppercase font-semibold text-amber-700 mb-1">
                    Observação
                  </div>
                  <div className="text-sm text-amber-900">{order.observation}</div>
                </div>
              )}
            </section>
          )}

          <section>
            <div className="text-[11px] uppercase font-semibold text-slate-400 tracking-wider mb-2">
              Valores
            </div>
            <div className={`grid gap-3 ${canSeeCost ? "grid-cols-3" : "grid-cols-1"}`}>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <div className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">Total</div>
                <div className="font-heading text-xl font-extrabold text-blue-800 tabular-nums">
                  R$ {fmtBRL(order.total)}
                </div>
              </div>
              {canSeeCost && (
                <>
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                    <div className="text-[10px] uppercase font-bold text-rose-600 tracking-wider">Custo</div>
                    <div className="font-heading text-xl font-extrabold text-rose-800 tabular-nums">
                      R$ {fmtBRL(order.costValue || 0)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Lucro</div>
                    <div className="font-heading text-xl font-extrabold text-emerald-800 tabular-nums">
                      R$ {fmtBRL((Number(order.total) || 0) - (Number(order.costValue) || 0))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {(order.batteryPercent || order.batteryMsg || order.telaMsg) && (
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                {order.batteryPercent && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                    Bateria: {order.batteryPercent}%
                  </span>
                )}
                {order.batteryMsg && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                    {order.batteryMsg === "com" ? "Bateria: com mensagem" : "Bateria: sem mensagem"}
                  </span>
                )}
                {order.telaMsg && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 font-semibold">
                    {order.telaMsg === "com" ? "Tela: com mensagem" : "Tela: sem mensagem"}
                  </span>
                )}
              </div>
            )}
          </section>

          {/* Photos */}
          <section className="border-t border-slate-100 pt-4">
            <OrderPhotos order={order} onChange={onRefresh} />
          </section>

          {order.status === "delivered" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-[11px] uppercase font-semibold text-emerald-700 mb-1">
                Entregue
              </div>
              <div className="text-sm text-emerald-900">
                {order.exitDate} às {order.exitTime} · Pagamento:{" "}
                <b>{PAY_LABELS[order.payMethod] || "—"}</b>
                {order.cartaoDesc ? ` (${order.cartaoDesc})` : ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
